import { FC, KeyboardEvent, useState } from "react"
import { X } from "lucide-react"
import BBSwitch from "../../../../../../form/Switch"
import { RateTableConfigDetails } from "../../../../../../../../types"

interface Props {
  values: RateTableConfigDetails
  setDetailsField: (field: string, value: any) => void
  removeDetailsField: (field: string) => void
}

interface FlagRowProps {
  label: string
  description?: string
  enabled: boolean
  onChange: (val: boolean) => void
  onDelete?: () => void
  last?: boolean
}

const FlagRow: FC<FlagRowProps> = ({
  label,
  description,
  enabled,
  onChange,
  onDelete,
  last
}) => (
  <div
    className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${
      last ? "" : "border-b border-gray-100"
    }`}
  >
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {description && <p className="text-xs text-gray-400">{description}</p>}
    </div>
    <div className="flex items-center gap-3 ml-4 shrink-0">
      <BBSwitch enabled={enabled} setEnabled={onChange} />
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="text-gray-300 hover:text-red-400 transition-colors text-lg leading-none"
          aria-label={`Remove ${label}`}
        >
          ×
        </button>
      )}
    </div>
  </div>
)

const FLAGS = [
  {
    key: "subscription_cta_enabled",
    label: "Subscription CTA",
    description: "Show rate alert subscription option"
  },
  {
    key: "google_login_enabled",
    label: "Google login",
    description: "Allow sign-in via Google OAuth"
  },
  {
    key: "featured_rates_tab_enabled",
    label: "Featured rates tab",
    description: "Show a featured rates tab in the embed"
  },
  {
    key: "fees_modal_disabled",
    label: "Disable fees modal",
    description: "Hide the fees breakdown modal"
  },
  {
    key: "validation_required",
    label: "Validation required",
    description: "Require email/phone verification before showing results"
  },
  {
    key: "exclude_govt_programs",
    label: "Exclude govt programs",
    description: "Hide government-backed loan programs from results"
  }
] as const

const FLAG_KEYS = new Set(FLAGS.map(f => f.key))

const KNOWN_NON_BOOLEAN_KEYS = new Set([
  "max_results",
  "usage_limit",
  "min_price",
  "sort_by_default_",
  "disabled_fields",
  "settings"
])

const RateTableGeneral: FC<Props> = ({
  values,
  setDetailsField,
  removeDetailsField
}) => {
  const d = values.details
  const [tagInput, setTagInput] = useState("")
  const [adding, setAdding] = useState(false)
  const [newKey, setNewKey] = useState("")

  const extraFields = Object.entries(d as Record<string, any>).filter(
    ([k, v]) =>
      typeof v === "boolean" &&
      !FLAG_KEYS.has(k as any) &&
      !KNOWN_NON_BOOLEAN_KEYS.has(k)
  ) as [string, boolean][]

  const handleAdd = () => {
    const key = newKey.trim()
    if (!key) return
    setDetailsField(key, false)
    setNewKey("")
    setAdding(false)
  }

  const addTag = () => {
    const val = tagInput.trim()
    if (!val) return
    const current = d.disabled_fields ?? []
    if (!current.includes(val)) {
      setDetailsField("disabled_fields", [...current, val])
    }
    setTagInput("")
  }

  const removeTag = (tag: string) => {
    setDetailsField(
      "disabled_fields",
      (d.disabled_fields ?? []).filter(f => f !== tag)
    )
  }

  const onTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addTag()
    }
  }

  return (
    <div className="space-y-6 pt-2">
      {/* Toggles */}
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        {FLAGS.map(({ key, label, description }, i) => (
          <FlagRow
            key={key}
            label={label}
            description={description}
            enabled={!!d[key]}
            onChange={val => setDetailsField(key, val)}
            last={i === FLAGS.length - 1}
          />
        ))}
      </div>

      {(extraFields.length > 0 || adding) && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500">Custom fields</p>
          </div>
          {extraFields.map(([key, value], i) => (
            <FlagRow
              key={key}
              label={key}
              enabled={value}
              onChange={val => setDetailsField(key, val)}
              last={i === extraFields.length - 1 && !adding}
              onDelete={() => removeDetailsField(key)}
            />
          ))}
          {adding && (
            <div className="flex items-center gap-2 px-4 py-3">
              <input
                autoFocus
                type="text"
                value={newKey}
                onChange={e => setNewKey(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleAdd()
                  if (e.key === "Escape") {
                    setAdding(false)
                    setNewKey("")
                  }
                }}
                placeholder="field_key"
                className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAdd}
                disabled={!newKey.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdding(false)
                  setNewKey("")
                }}
                className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {!adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-primary-600 transition-colors"
        >
          <span className="text-base leading-none">+</span>
          Add field
        </button>
      )}

      {/* Numeric fields */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            Max results
          </label>
          <input
            type="number"
            min={1}
            value={d.max_results ?? ""}
            onChange={e =>
              setDetailsField(
                "max_results",
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            Usage limit
          </label>
          <input
            type="number"
            min={0}
            value={d.usage_limit ?? ""}
            onChange={e =>
              setDetailsField(
                "usage_limit",
                e.target.value ? Number(e.target.value) : 0
              )
            }
            className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">
            Min price{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="number"
            min={0}
            value={d.min_price ?? ""}
            onChange={e =>
              setDetailsField(
                "min_price",
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      {/* Sort by default */}
      <div className="max-w-xs">
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Sort by default{" "}
          <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={d.sort_by_default_ ?? ""}
          onChange={e =>
            setDetailsField("sort_by_default_", e.target.value || undefined)
          }
          placeholder="e.g. low_rate"
          className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Disabled fields tags */}
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1">
          Disabled fields
        </label>
        <p className="text-xs text-gray-400 mb-2">
          Type a field name and press Enter to disable it in the embed
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(d.disabled_fields ?? []).map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 max-w-xs">
          <input
            type="text"
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={onTagKeyDown}
            placeholder="property_type"
            className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            type="button"
            onClick={addTag}
            className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  )
}

export default RateTableGeneral
